import Button from '@/components/Buttons/Button';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
    AvatarList,
    CopySnackbar,
    createClipboardHandler,
    createDragHandlers,
    EmptyState,
    FilterContainer,
    LoadingState,
} from '../shared/AvatarsOrdering';
import { AvatarItem } from './components/AvatarItem';
import { GroupItem } from './components/GroupItem';
import { InstructionsSection } from './components/InstructionsSection';
import { JsonOutputTabs } from './components/JsonOutputTabs';
import { useAvatarOrdering } from './hooks/useAvatarOrdering';
import { useJsonOutput } from './hooks/useJsonOutput';

export const AvatarOrderingForm = () => {
    const {
        publicAvatars,
        isLoading,
        orderedItems,
        setOrderedItems,
        buildOrderedItems,
        handleDragEnd,
        createGroup,
        ungroupAvatar,
        removeFromGroup,
        resetToDefaults,
    } = useAvatarOrdering();

    const { generateJsonOutput } = useJsonOutput(orderedItems);
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const [jsonTab, setJsonTab] = useState(0);

    const { handleDragStart, handleDragEndCleanup } = createDragHandlers();

    useEffect(() => {
        const items = buildOrderedItems();
        setOrderedItems(items);
    }, [buildOrderedItems, setOrderedItems]);

    const copyToClipboard = createClipboardHandler(() => {
        const jsonType = jsonTab === 0 ? 'top_avatars' : 'avatars_grouping';
        return generateJsonOutput(jsonType);
    }, setShowCopySuccess);

    const onDragEnd = (result: any) => {
        handleDragEnd(result, handleDragEndCleanup);
    };

    if (isLoading) {
        return <LoadingState isLoading={isLoading} />;
    }

    if (publicAvatars.length === 0) {
        return <EmptyState message="No avatars found." />;
    }

    return (
        <Box>
            <InstructionsSection />

            <FilterContainer>
                <Button variant="outlined" onClick={resetToDefaults} size="small">
                    Reset to Current LD Values
                </Button>
            </FilterContainer>

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Ordered Avatars & Groups ({orderedItems.length})
            </Typography>

            <DragDropContext onDragEnd={onDragEnd} onDragStart={handleDragStart}>
                <Droppable droppableId="ordered-items" type="AVATAR">
                    {(provided, snapshot) => (
                        <AvatarList
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                                backgroundColor: snapshot.isDraggingOver ? 'primary.light' : 'transparent',
                                transition: 'background-color 0.2s ease',
                            }}>
                            {orderedItems.map((item, index) => {
                                if (item.type === 'avatar') {
                                    const avatar = item.avatar!;
                                    return (
                                        <Draggable key={avatar.id} draggableId={avatar.id} index={index}>
                                            {(provided, snapshot) => (
                                                <AvatarItem
                                                    avatar={avatar}
                                                    index={index}
                                                    provided={provided}
                                                    snapshot={snapshot}
                                                    onCreateGroup={createGroup}
                                                />
                                            )}
                                        </Draggable>
                                    );
                                } else {
                                    const group = item.group!;
                                    return (
                                        <Box key={group.id}>
                                            <Draggable draggableId={group.mainAvatar.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <GroupItem
                                                        group={group}
                                                        index={index}
                                                        provided={provided}
                                                        snapshot={snapshot}
                                                        onUngroupAvatar={ungroupAvatar}
                                                        onRemoveFromGroup={removeFromGroup}
                                                    />
                                                )}
                                            </Draggable>
                                        </Box>
                                    );
                                }
                            })}
                            {provided.placeholder}
                        </AvatarList>
                    )}
                </Droppable>
            </DragDropContext>

            <JsonOutputTabs
                jsonTab={jsonTab}
                onTabChange={setJsonTab}
                topAvatarsJson={generateJsonOutput('top_avatars')}
                avatarsGroupingJson={generateJsonOutput('avatars_grouping')}
                onCopy={copyToClipboard}
            />

            <CopySnackbar open={showCopySuccess} onClose={() => setShowCopySuccess(false)} />
        </Box>
    );
};
